import { HttpError } from '@euler/lib/request/HttpError';
import { BiCardQuery } from '@euler/model/bi/blend';
import {
  BiApplyOpsRequest,
  BiApplyOpsResult,
  BiCardData,
  BiCardDataRequest,
  BiCardDataState,
  BiCardView,
  BiCreateDashboardRequest,
  BiDashboardEntry,
  BiDashboardGrantPermission,
  BiDashboardGrantView,
  BiDashboardOpKind,
  BiDashboardPermission,
  BiDashboardRevisionView,
  BiDashboardSummary,
  BiDashboardView,
  BiDashboardVisibility,
  BiDateFilterUse,
  BiLayoutMode,
  BiReplaceGrantsRequest,
  BiRevisionSource,
  BiUndoRequest,
} from '@euler/model/bi/dashboard';
import { BiCreateDraftRequest, BiDraftView } from '@euler/model/bi/draft';
import {
  BiDatasetDetail,
  BiDatasetList,
  BiDatasetSection,
  BiListDatasetsRequest,
} from '@euler/model/bi/library';
import {
  BiTimeGrain,
  BiTimeRange,
  BiTimeRangeKind,
} from '@euler/model/bi/query';
import { BiQueryResult } from '@euler/model/bi/result';
import { BiPalette } from '@euler/model/bi/viz';
import { FixtureBiService } from '@euler/playground/bi-visualization/FixtureBiService';
import { kBiBuiltinDatasetsFixture } from './biBuiltinDatasetsFixture';
import { biFixtureQuery } from './biCatalogFixtures';
import {
  kBiComboCardDataCaptured,
  kBiComboDashboardCaptured,
} from './biComboCaptured';
import {
  biFixtureCardResult,
  biFixtureDashboards,
  kFailingCardName,
  kRestrictedCardName,
  kUntimedCardName,
} from './biDashboardFixtures';
import { biFixturePlaceCard } from './biFixturePlaceCard';
import {
  kBiRollingAbsoluteCaptured,
  kBiRollingCardDataCaptured,
  kBiRollingDashboardCaptured,
  kBiRollingFilteredCaptured,
} from './biRollingCaptured';
import {
  kBiStyleCardDataCaptured,
  kBiStyleDashboardCaptured,
} from './biStyleCaptured';
import {
  kBiMineDatasetsCaptured,
  kBiUploadedDetailsCaptured,
} from './biUploadedCaptured';

function httpError(code: string, subCode: string, message: string) {
  const error = new HttpError(message);
  error.code = code;
  error.subCode = subCode;
  return error;
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const now = () => new Date().toISOString();

/** Dashboards captured from the dev rear, and their cards' data. */
const kCapturedDashboards = [
  kBiStyleDashboardCaptured,
  kBiComboDashboardCaptured,
  kBiRollingDashboardCaptured,
];
const kCapturedCardData: Record<string, BiCardData> = {
  ...kBiStyleCardDataCaptured,
  ...kBiComboCardDataCaptured,
  ...kBiRollingCardDataCaptured,
};
/**
 * The rolling card under a dashboard date range, as captured: the range's
 * dates replace the card's own in its caption.
 */
const kCapturedFiltered = [
  kBiRollingFilteredCaptured,
  kBiRollingAbsoluteCaptured,
];

/** Whether a captured filtered load answers this range. */
function biFixtureWindowMatches(range: BiTimeRange, data: BiCardData) {
  if (data.state !== BiCardDataState.Ready) return false;
  const window = data.result.current.window;
  if (range.kind === BiTimeRangeKind.Absolute)
    return range.from === window?.from && range.to === window?.to;
  return (
    range.kind === BiTimeRangeKind.Rolling &&
    range.unit === BiTimeGrain.Month &&
    range.count === 9 &&
    !range.includeCurrent
  );
}

/** A value as JSON with every object's keys sorted. */
function stableKey(value: unknown): string {
  return JSON.stringify(value, (_, entry) =>
    entry && typeof entry === 'object' && !Array.isArray(entry)
      ? Object.fromEntries(
          Object.keys(entry)
            .sort()
            .map(key => [key, entry[key]]),
        )
      : entry,
  );
}

/** A captured card's spec answers a query with the card's rows. */
const kCapturedQueries = new Map(
  kCapturedDashboards.flatMap(item =>
    item.cards.flatMap(card => {
      const data = kCapturedCardData[card.id];
      return data?.state === BiCardDataState.Ready
        ? [[stableKey(card.spec), data.result.current] as const]
        : [];
    }),
  ),
);

interface Revision {
  view: BiDashboardView;
  source: BiRevisionSource;
  at: string;
}

/**
 * Dashboards in memory, so the pages render every state without a server:
 * a failing card, a restricted one and one that ignores the date filter;
 * ops, revisions, undo and grants behave like the server's.
 */
export class FixtureBiDashboardService extends FixtureBiService {
  private readonly store = new Map(
    [...biFixtureDashboards(), ...kCapturedDashboards].map(item => [
      item.id,
      item,
    ]),
  );
  private readonly history = new Map<string, Revision[]>(
    [...this.store.values()].map(item => [
      item.id,
      [{ view: item, source: BiRevisionSource.Create, at: item.createdAt }],
    ]),
  );
  private readonly grants = new Map<string, BiDashboardGrantView[]>();
  private readonly drafts = new Map<string, BiDraftView>();
  private readonly results = new Map<string, BiQueryResult>();

  /**
   * `conflictOnce`: one apply is refused as stale, as if a colleague saved
   * first. `catalogsFailOnce`: the first catalog read fails.
   */
  constructor(
    private conflictOnce = false,
    private catalogsFailOnce = false,
    /** Every dashboard is shared with the caller to view only. */
    private readonly viewer = false,
  ) {
    super();
  }

  async dashboards(): Promise<BiDashboardSummary[]> {
    await delay(150);
    return [...this.store.values()].map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      visibility: item.visibility,
      ownerMemberId: item.ownerMemberId,
      ownerName: item.ownerName,
      subjectMemberId: item.subjectMemberId,
      subjectName: item.subjectName,
      subjectRole: item.subjectRole,
      isDefault: item.isDefault,
      revision: item.revision,
      permission: item.permission,
      updatedAt: item.updatedAt,
      cardCount: item.cards.length,
    }));
  }
  async dashboardEntry(): Promise<BiDashboardEntry> {
    await delay(150);
    const items = [...this.store.values()];
    return {
      dashboardId: (items.find(item => item.isDefault) ?? items[0])?.id ?? null,
    };
  }
  async setDefaultDashboard(dashboardId: string): Promise<BiDashboardView> {
    for (const item of this.store.values())
      this.store.set(item.id, { ...item, isDefault: item.id === dashboardId });
    return await this.dashboard(dashboardId);
  }
  async createDashboard(
    request: BiCreateDashboardRequest,
  ): Promise<BiDashboardView> {
    const created: BiDashboardView = {
      id: `bidash_${String(Date.now()).padEnd(26, '0')}`,
      name: request.name,
      description: null,
      visibility: request.visibility ?? BiDashboardVisibility.Private,
      subjectMemberId: null,
      subjectName: null,
      subjectRole: null,
      ownerMemberId: 1,
      ownerName: '王店长',
      isDefault: false,
      revision: 0,
      permission: BiDashboardPermission.Manage,
      updatedAt: now(),
      createdAt: now(),
      filters: {},
      palette: BiPalette.Default,
      cards: [],
      layout: { mode: BiLayoutMode.Preset, columns: 'auto', order: [] },
    };
    this.store.set(created.id, created);
    this.history.set(created.id, [
      { view: created, source: BiRevisionSource.Create, at: created.createdAt },
    ]);
    return created;
  }
  async dashboard(dashboardId: string): Promise<BiDashboardView> {
    await delay(150);
    const view = this.get(dashboardId);
    return this.viewer
      ? { ...view, permission: BiDashboardPermission.View }
      : view;
  }
  async deleteDashboard(dashboardId: string): Promise<void> {
    this.store.delete(dashboardId);
  }
  async applyDashboardOps(
    dashboardId: string,
    request: BiApplyOpsRequest,
  ): Promise<BiApplyOpsResult> {
    await delay(200);
    const current = this.get(dashboardId);
    if (this.conflictOnce) {
      this.conflictOnce = false;
      this.commit(
        { ...current, revision: current.revision + 1, updatedAt: now() },
        BiRevisionSource.Manual,
      );
    }
    if (request.baseRevision !== this.get(dashboardId).revision)
      throw httpError(
        'resource_state_conflict',
        'bi_dashboard_revision_conflict',
        '看板已被修改，请刷新后重试。',
      );
    let next: BiDashboardView = { ...current };
    const addedCards = [];
    for (const op of request.ops) {
      switch (op.op) {
        case BiDashboardOpKind.RenameDashboard:
          next = { ...next, name: op.name };
          break;
        case BiDashboardOpKind.SetLayout:
          next = { ...next, layout: op.layout };
          break;
        case BiDashboardOpKind.PlaceCard:
          next = {
            ...next,
            layout: biFixturePlaceCard(next.layout, op.cardId, op.placement),
          };
          break;
        case BiDashboardOpKind.SetFilters:
          next = { ...next, filters: op.filters };
          break;
        case BiDashboardOpKind.SetPalette:
          next = { ...next, palette: op.palette };
          break;
        case BiDashboardOpKind.RemoveCard:
          next = {
            ...next,
            cards: next.cards.filter(card => card.id !== op.cardId),
            layout:
              next.layout.mode === BiLayoutMode.Preset
                ? {
                    ...next.layout,
                    order: next.layout.order.filter(id => id !== op.cardId),
                  }
                : {
                    ...next.layout,
                    items: next.layout.items.filter(
                      item => item.cardId !== op.cardId,
                    ),
                  },
          };
          break;
        case BiDashboardOpKind.UpdateCard:
          next = {
            ...next,
            cards: next.cards.map(card =>
              card.id === op.cardId
                ? {
                    ...card,
                    title: op.title ?? card.title,
                    spec: op.querySpec ?? card.spec,
                    viz: op.vizSpec ?? card.viz,
                    updatedAt: now(),
                  }
                : card,
            ),
          };
          if (op.querySpec)
            this.results.set(op.cardId, biFixtureQuery(op.querySpec));
          break;
        case BiDashboardOpKind.AddCard: {
          const draft = this.drafts.get(op.draftId)!;
          const card: BiCardView = {
            id: `bicard_${String(Date.now()).padEnd(26, '0')}`,
            title: draft.title,
            spec: draft.spec,
            viz: draft.viz,
            sourceDraftId: draft.id,
            createdBy: 1,
            createdAt: now(),
            updatedAt: now(),
          };
          this.results.set(card.id, draft.result.current);
          addedCards.push({ draftId: draft.id, cardId: card.id });
          next = {
            ...next,
            cards: [...next.cards, card],
            layout:
              next.layout.mode === BiLayoutMode.Preset
                ? { ...next.layout, order: [...next.layout.order, card.id] }
                : {
                    ...next.layout,
                    items: [
                      ...next.layout.items,
                      {
                        cardId: card.id,
                        x: 0,
                        y: Math.max(
                          0,
                          ...next.layout.items.map(item => item.y + item.h),
                        ),
                        w: 6,
                        h: 4,
                      },
                    ],
                  },
          };
          break;
        }
        default:
          break;
      }
    }
    next = { ...next, revision: current.revision + 1, updatedAt: now() };
    this.commit(
      next,
      addedCards.length ? BiRevisionSource.Add : BiRevisionSource.Manual,
    );
    return { dashboard: next, addedCards };
  }
  async dashboardRevisions(
    dashboardId: string,
  ): Promise<BiDashboardRevisionView[]> {
    await delay(150);
    return (this.history.get(dashboardId) ?? [])
      .map(entry => ({
        revision: entry.view.revision,
        source: entry.source,
        changeSetId: null,
        createdBy: 1,
        createdByName: '王店长',
        createdAt: entry.at,
        cardCount: entry.view.cards.length,
      }))
      .reverse();
  }
  async undoDashboard(
    dashboardId: string,
    request: BiUndoRequest,
  ): Promise<BiDashboardView> {
    await delay(200);
    const current = this.get(dashboardId);
    const history = this.history.get(dashboardId)!;
    if (request.revision !== current.revision || history.length < 2)
      throw httpError(
        'resource_state_conflict',
        'bi_dashboard_revision_conflict',
        '看板已被修改，请刷新后重试。',
      );
    const previous = history[history.length - 2].view;
    const next = {
      ...previous,
      revision: current.revision + 1,
      updatedAt: now(),
    };
    this.commit(next, BiRevisionSource.Undo);
    return next;
  }
  async setDashboardVisibility(
    dashboardId: string,
    visibility: BiDashboardVisibility,
  ): Promise<BiDashboardView> {
    const next = { ...this.get(dashboardId), visibility };
    this.store.set(dashboardId, next);
    return next;
  }
  async dashboardGrants(dashboardId: string): Promise<BiDashboardGrantView[]> {
    await delay(150);
    return (
      this.grants.get(dashboardId) ?? [
        {
          memberId: 12,
          permission: BiDashboardGrantPermission.Edit,
          memberName: '李主管',
          createdBy: 1,
          createdAt: now(),
        },
      ]
    );
  }
  async replaceDashboardGrants(
    dashboardId: string,
    request: BiReplaceGrantsRequest,
  ): Promise<BiDashboardGrantView[]> {
    const views = request.grants.map(grant => ({
      ...grant,
      memberName: null,
      createdBy: 1,
      createdAt: now(),
    }));
    this.grants.set(dashboardId, views);
    return views;
  }
  async query(spec: BiCardQuery): Promise<BiQueryResult> {
    await delay(250);
    return kCapturedQueries.get(stableKey(spec)) ?? biFixtureQuery(spec);
  }
  async createDraft(request: BiCreateDraftRequest): Promise<BiDraftView> {
    const draft: BiDraftView = {
      id: `bidraft_${String(Date.now()).padEnd(26, '0')}`,
      title: request.title,
      spec: request.spec,
      viz: request.viz,
      result: { current: biFixtureQuery(request.spec) },
      parentDraftId: null,
      rationale: null,
      createdAt: now(),
      expiresAt: now(),
    };
    this.drafts.set(draft.id, draft);
    return draft;
  }
  async listDatasets(request: BiListDatasetsRequest): Promise<BiDatasetList> {
    await delay(150);
    if (this.catalogsFailOnce) {
      this.catalogsFailOnce = false;
      throw httpError(
        'service_unavailable',
        'service_unavailable',
        '服务暂不可用',
      );
    }
    switch (request.section) {
      case BiDatasetSection.Builtin:
        return kBiBuiltinDatasetsFixture;
      case BiDatasetSection.Mine:
        return kBiMineDatasetsCaptured;
      case BiDatasetSection.Shared:
        return { section: BiDatasetSection.Shared, datasets: [] };
    }
  }
  async getDataset(ref: string): Promise<BiDatasetDetail> {
    await delay(200);
    const found = kBiUploadedDetailsCaptured.find(
      detail => detail.dataset.id === ref,
    );
    if (!found)
      throw httpError('resource_not_found', 'record_not_found', '记录不存在!');
    return found;
  }
  async cardData(
    cardId: string,
    request: BiCardDataRequest,
  ): Promise<BiCardData> {
    await delay(300 + Math.random() * 700);
    if (cardId.includes(kFailingCardName))
      throw httpError(
        'invalid_query_parameter_value',
        'bi_query_invalid',
        '这张图表的查询已失效：字段「施工时长」不再可用。',
      );
    if (cardId.includes(kRestrictedCardName))
      return { state: BiCardDataState.Restricted, cardId };
    const captured = kCapturedCardData[cardId];
    const own = this.results.get(cardId);
    if (captured && !own && !request.filters) return captured;
    const filtered = kCapturedFiltered.find(
      data =>
        data.cardId === cardId &&
        data.state === BiCardDataState.Ready &&
        request.filters?.dateRange &&
        data.result.current.window &&
        biFixtureWindowMatches(request.filters.dateRange, data),
    );
    if (filtered) return filtered;
    const result = own
      ? { current: own }
      : captured?.state === BiCardDataState.Ready
      ? captured.result
      : biFixtureCardResult(cardId);
    if (!result)
      throw httpError('resource_not_found', 'record_not_found', '记录不存在!');
    const dateRange = request.filters
      ? request.filters.dateRange
      : [...this.store.values()].find(item =>
          item.cards.some(card => card.id === cardId),
        )?.filters.dateRange;
    return {
      state: BiCardDataState.Ready,
      cardId,
      result,
      dateFilter: !dateRange
        ? BiDateFilterUse.None
        : cardId.includes(kUntimedCardName)
        ? BiDateFilterUse.Ignored
        : BiDateFilterUse.Applied,
    };
  }

  private get(dashboardId: string): BiDashboardView {
    const found = this.store.get(dashboardId);
    if (!found)
      throw httpError('resource_not_found', 'record_not_found', '记录不存在!');
    return found;
  }
  private commit(next: BiDashboardView, source: BiRevisionSource) {
    this.store.set(next.id, next);
    this.history.get(next.id)!.push({ view: next, source, at: next.updatedAt });
  }
}
